import React, { useState } from "react";
import { Upload, Image, message } from "antd";
import classNames from "classnames";

const { Dragger } = Upload;

/* Base64 for local preview */
const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const AntDUpload = ({
  type = "button",          // button | dragger
  listType = "text",        // text | picture | picture-card

  className = "",           // Custom class
  disabled = false,         // Disable upload

  fileList = [],            // File list
  onChange,

  accept,                   // Accept file types (i.e .pdf,.docx,.jpg,.png)
  multiple = false,         // Allow multiple files

  minFiles,                 // Minimum files
  maxFiles,                 // Maximum files
  maxSizeMB,                // Maximum file size in MB

  autoUpload = false,       // Auto upload

  enableImagePreview = false, // Enable image preview

  beforeUpload,           // custom hook if needed

  children,
  ...rest
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  /* ---------- Preview ---------- */
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  /* ---------- Validations ---------- */
  const handleBeforeUpload = (file) => {

    // ✅ Duplicate check
    const isDuplicate = fileList.some(
      (f) => f.name === file.name && f.size === file.size
    );

    if (isDuplicate) {
      message.warning("This file is already added");
      return Upload.LIST_IGNORE;
    }

    // ✅ File type validation
    if (accept) {
      const acceptedTypes = accept.split(",").map(t => t.trim());

      const isValidType = acceptedTypes.some(type => {
        if (type === "image/*") return file.type.startsWith("image/");
        return file.type === type || file.name.endsWith(type);
      });

      if (!isValidType) {
        message.error("Invalid file type");
        return Upload.LIST_IGNORE;
      }
    }

    // ✅ File size validation
    if (maxSizeMB && file.size / 1024 / 1024 > maxSizeMB) {
      message.error(`File must be smaller than ${maxSizeMB}MB`);
      return Upload.LIST_IGNORE;
    }

    // ✅ Max files validation
    if (maxFiles && fileList.length >= maxFiles) {
      message.error(`Maximum ${maxFiles} files allowed`);
      return Upload.LIST_IGNORE;
    }

    if (beforeUpload) {
      return beforeUpload(file);
    }

    return autoUpload;
  };



  const commonProps = {
    disabled,
    fileList,
    onChange,
    accept,
    multiple,
    listType,
    beforeUpload: handleBeforeUpload,
    onPreview: enableImagePreview ? handlePreview : undefined,
    className: classNames("SwAntDUpload", className),
    ...rest,
  };

  return (
    <>
      {type === "dragger" ? (
        <Dragger {...commonProps}>{children}</Dragger>
      ) : (
        <Upload {...commonProps}>{children}</Upload>
      )}

      {/* Image Preview */}
      {enableImagePreview && previewImage && (
        <Image
          styles={{ root: { display: "none" } }}
          preview={{
            open: previewOpen,
            onOpenChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) =>
              !visible && setPreviewImage(""),
          }}
          src={previewImage}
        />
      )}
    </>
  );
};

export default AntDUpload;
